import { useEffect, useState } from 'react'
import { format, getMonth } from 'date-fns'
import { DayPicker } from 'react-day-picker'
import { enGB } from 'react-day-picker/locale'
import 'react-day-picker/style.css'

import styles from './DatePicker.module.scss'
import { Weekdays } from './components/Weekdays'
import { NextMonthButton } from './components/NextMonthButton'
import { PreviousMonthButton } from './components/PreviousMonthButton'
import stylesMileage from './DatePickerMileage.module.scss'
import { useGetMileagePeriod } from '@/modules/CombinedLogs'
import ChevronRightIcon from '../assets/chevron-right.svg'
import LockIcon from '../assets/lock.svg'
import CircleIcon from '../assets/circle.svg'
import DangerFlagIcon from '@/modules/CombinedLogs/assets/danger-flag.svg'
import FlagIcon from '@/modules/CombinedLogs/assets/flag.svg'
import clsx from 'clsx'

interface Props {
    onChange?: (date: Date) => void
    value?: Date
    className?: string
    isIndexPage?: boolean
}

export const DatePickerPM = ({ value = new Date(), onChange, className, isIndexPage }: Props) => {
    const [selectedMonth, setSelectedMonth] = useState(value)
    const { data: periods } = useGetMileagePeriod()

    const handleDateChange = (selected: Date) => onChange && onChange(selected)
    const handleSelectedMonthChange = (selected: Date) => setSelectedMonth(selected)

    useEffect(() => {
        if (getMonth(value) !== getMonth(selectedMonth)) {
            setSelectedMonth(value)
        }
    }, [value])

    const handlePeriodClick = (date: string) => {
        onChange && onChange(new Date(date))
    }

    return (
        <div className={stylesMileage.rootMileage}>
            <div className={stylesMileage.periodsContainer}>
                <div className={stylesMileage.title}>Mileage period</div>

                <div>
                    {periods?.periods.map((period) => (
                        <button
                            className={stylesMileage.period}
                            key={period.date}
                            onClick={() => handlePeriodClick(period.date)}
                            disabled={period.summary.isEmpty || period.summary.isLocked || period.isToday}
                        >
                            <div
                                className={clsx(stylesMileage.periodDate, {
                                    [stylesMileage.periodDateFriday]: period.summary.isEmpty || period.isToday,
                                })}
                            >
                                {period.dayOfWeek} {format(period.date, 'dd')}
                                {period.summary.isLocked && <LockIcon />}
                            </div>

                            {!isIndexPage && (
                                <div
                                    className={clsx(stylesMileage.periodInfo, {
                                        [stylesMileage.periodInfoFriday]: period.summary.isEmpty || period.isToday,
                                    })}
                                >
                                    {!period.isToday && period.summary.activeFlagCount > 0 && (
                                        <span className={stylesMileage.periodCount}>
                                            <DangerFlagIcon />
                                            {period.summary.activeFlagCount}
                                        </span>
                                    )}
                                    {!period.isToday && period.summary.doneFlagCount > 0 && (
                                        <span className={clsx(stylesMileage.periodCount, stylesMileage.periodCountDone)}>
                                            <FlagIcon />
                                            {period.summary.doneFlagCount}
                                        </span>
                                    )}
                                    {!period.isToday && period.summary.unfinishedCount > 0 && (
                                        <span className={stylesMileage.periodCount}>
                                            <CircleIcon />
                                            {period.summary.unfinishedCount}
                                        </span>
                                    )}
                                    <ChevronRightIcon />
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            <DayPicker
                required
                onSelect={handleDateChange}
                onMonthChange={handleSelectedMonthChange}
                mode="single"
                animate
                showOutsideDays
                selected={value}
                month={selectedMonth}
                locale={enGB}
                disabled={{ after: new Date() }}
                className={clsx(stylesMileage.datePickerContainer, className)}
                classNames={{
                    caption_label: styles.label,
                    day: styles.day,
                    day_button: styles.dayButton,
                    outside: styles.outside,
                    nav: styles.nav,
                    month_caption: styles.caption,
                    selected: styles.daySelected,
                    today: styles.today,
                    disabled: styles.outside,
                }}
                components={{
                    NextMonthButton: () => <NextMonthButton selectedMonth={selectedMonth} />,
                    PreviousMonthButton,
                    Weekdays,
                }}
            />
        </div>
    )
}
